// src/components/Blog/Blog.tsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { QueryDocumentSnapshot, DocumentData } from 'firebase/firestore';
import Header from '../Header/Header';
import Footer from '../Footer/Footer';
import BackToTop from '../Back-to-top/BackToTop';
import FeaturedProducts from '../Feature-Products/FeaturedProducts';
import BlogImage from '../shared/BlogImage';
import {
  useBlogsPage,
  useBlogSearch,
  BlogPost,
  PAGE_SIZE,
} from '../../Hooks/useBlog';

/* ── Component ── */

const Blog: React.FC = () => {
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  // Cursor array — kept in component state because Firestore cursor docs
  // cannot be serialised into a React Query key.
  const [pageCursors, setPageCursors] = useState<
    (QueryDocumentSnapshot<DocumentData> | null)[]
  >([]);

  /* ── Queries ── */

  const {
    data: pageData,
    isLoading: pageLoading,
  } = useBlogsPage(currentPage, pageCursors);

  const {
    data: searchResults,
    isLoading: searchLoading,
  } = useBlogSearch(activeSearch, isSearching);

  // Store cursor returned by each page fetch so next/prev navigation works.
  React.useEffect(() => {
    if (pageData?.cursor) {
      setPageCursors(prev => {
        const updated = [...prev];
        updated[currentPage - 1] = pageData.cursor;
        return updated;
      });
    }
  }, [pageData, currentPage]);

  const blogs: BlogPost[] = isSearching ? (searchResults ?? []) : (pageData?.blogs ?? []);
  const hasMore = isSearching ? false : (pageData?.hasMore ?? false);
  const loading = isSearching ? searchLoading : pageLoading;

  /* ── Handlers ── */

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchTerm.trim()) {
      handleClearSearch();
      return;
    }
    setActiveSearch(searchTerm);
    setIsSearching(true);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setActiveSearch('');
    setIsSearching(false);
    setCurrentPage(1);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(p => p - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleNextPage = () => {
    if (hasMore) {
      setCurrentPage(p => p + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  /* ── Helpers ── */

  const formatDate = (timestamp: any) => {
    if (!timestamp) return '';
    const date =
      typeof timestamp.toDate === 'function'
        ? timestamp.toDate()
        : new Date(timestamp.seconds * 1000);
    return date.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getFirstParagraph = (paragraphs: BlogPost['paragraphs']) => {
    if (!paragraphs?.length) return '';
    const sorted = [...paragraphs].sort((a, b) => a.order - b.order);
    return sorted[0].content.length > 150
      ? sorted[0].content.substring(0, 150) + '...'
      : sorted[0].content;
  };

  const handleBlogClick = (blog: BlogPost) => {
    navigate(`/blog/${blog.slug}`, { state: { blog } });
  };

  /* ── Render ── */

  return (
    <div>
      <Header />

      <section
        className="bg-img1 txt-center p-lr-15 p-tb-92"
        style={{ backgroundImage: `url('asset/images/bg-10.png')` }}
      >
      </section>

      <section className="bg0 p-t-62 p-b-60">
        <div className="container">
          <div className="row">

            {/* ── Blog List ── */}
            <div className="col-md-8 col-lg-9 p-b-80">
              <div className="p-r-45 p-r-0-lg">

                {isSearching && (
                  <div className="p-b-20">
                    <p className="stext-117 cl6">
                      Found {blogs.length} result(s) for "{activeSearch}"
                      <button
                        onClick={handleClearSearch}
                        className="stext-101 cl2 hov-cl1 trans-04 m-l-10"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
                      >
                        Clear Search
                      </button>
                    </p>
                  </div>
                )}

                {loading ? (
                  <p className="stext-117 cl6">Loading blogs...</p>
                ) : blogs.length === 0 ? (
                  <p className="stext-117 cl6">No blogs found.</p>
                ) : (
                  <>
                    {blogs.map(blog => (
                      <div key={blog.id} className="p-b-63">
                        <div
                          className="hov-img0 how-pos5-parent"
                          onClick={() => handleBlogClick(blog)}
                          style={{ cursor: 'pointer' }}
                        >
                          <BlogImage src={blog.backgroundImageUrl} alt={blog.title} />
                          <div className="flex-col-c-m size-123 bg9 how-pos5">
                            <span className="ltext-107 cl2">
                              {formatDate(blog.createdAt).split(' ')[1]}
                            </span>
                            <span className="stext-109 cl3">
                              {formatDate(blog.createdAt).split(' ')[0]}{' '}
                              {formatDate(blog.createdAt).split(' ')[2]}
                            </span>
                          </div>
                        </div>

                        <div className="p-t-32">
                          <h4 className="p-b-15">
                            <a
                              onClick={() => handleBlogClick(blog)}
                              className="ltext-108 cl2 hov-cl1 trans-04"
                              style={{ cursor: 'pointer' }}
                            >
                              {blog.title}
                            </a>
                          </h4>
                          <p className="stext-117 cl6">{getFirstParagraph(blog.paragraphs)}</p>
                          <div className="flex-w flex-sb-m p-t-18">
                            <span className="stext-111 cl2">
                              By {blog.authorName} | {formatDate(blog.createdAt)}
                            </span>
                            <a
                              onClick={() => handleBlogClick(blog)}
                              className="stext-101 cl2 hov-cl1 trans-04"
                              style={{ cursor: 'pointer' }}
                            >
                              Continue Reading →
                            </a>
                          </div>
                        </div>
                      </div>
                    ))}

                    {!isSearching && (currentPage > 1 || hasMore) && (
                      <div className="flex-l-m flex-w w-full p-t-10 m-lr--7">
                        {currentPage > 1 && (
                          <a
                            onClick={handlePreviousPage}
                            className="flex-c-m how-pagination1 trans-04 m-all-7"
                            style={{ cursor: 'pointer' }}
                          >
                            {currentPage - 1}
                          </a>
                        )}
                        <a
                          className="flex-c-m how-pagination1 trans-04 m-all-7 active-pagination1"
                          style={{ cursor: 'default' }}
                        >
                          {currentPage}
                        </a>
                        {hasMore && (
                          <a
                            onClick={handleNextPage}
                            className="flex-c-m how-pagination1 trans-04 m-all-7"
                            style={{ cursor: 'pointer' }}
                          >
                            {currentPage + 1}
                          </a>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* ── Sidebar ── */}
            <div className="col-md-4 col-lg-3 p-b-80">
              <div className="side-menu">
                <form className="p-b-65" onSubmit={handleSearchSubmit}>
                  <div className="bor17 of-hidden pos-relative">
                    <input
                      className="stext-103 cl2 plh4 size-116 p-l-28 p-r-55"
                      type="text"
                      placeholder="Search"
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                    />
                    <button className="flex-c-m size-122 ab-t-r fs-18 cl4">
                      <i className="zmdi zmdi-search"></i>
                    </button>
                  </div>
                </form>
                <FeaturedProducts />
              </div>
            </div>

          </div>
        </div>
      </section>

      <Footer />
      <BackToTop />
    </div>
  );
};

export default Blog;